/**
 * WebSocket hook for my_tasks channel.
 * Implements ticket-based auth, offline handling, and reconnection with exponential backoff.
 *
 * Per CLAUDE.md WebSocket Protocol:
 * - Ticket-based authentication (POST /api/v1/ws-ticket)
 * - Exponential backoff with jitter for reconnection
 * - Cursor-based resume for message continuity
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOnlineStatus } from './useOnlineStatus';
import { fetchWsTicket, getWebSocketUrl, ApiError } from '../lib/api';

// Server → Client message types
interface HelloMessage {
  type: 'hello';
  connectionId: string;
  serverTime: string;
  heartbeatSeconds: number;
}

interface SubscribedMessage {
  type: 'subscribed';
  channel: string;
  cursor: string;
}

interface ErrorMessage {
  type: 'error';
  code: string;
  message: string;
  retryAfterSeconds?: number;
}

interface PingMessage {
  type: 'ping';
}

interface ScheduleUpdatedMessage {
  type: 'schedule_updated';
  cursor: string;
  changes?: Array<{
    taskId: string;
    type: 'status' | 'assignment' | 'schedule';
  }>;
}

interface CursorExpiredMessage {
  type: 'cursor_expired';
  channel: string;
}

type ServerMessage =
  | HelloMessage
  | SubscribedMessage
  | ErrorMessage
  | PingMessage
  | ScheduleUpdatedMessage
  | CursorExpiredMessage;

// Client → Server message types
interface SubscribeMessage {
  type: 'subscribe';
  channel: string;
  cursor?: string;
}

interface UnsubscribeMessage {
  type: 'unsubscribe';
  channel: string;
}

interface PongMessage {
  type: 'pong';
}

type ClientMessage = SubscribeMessage | UnsubscribeMessage | PongMessage;

export interface WebSocketState {
  isConnected: boolean;
  isConnecting: boolean;
  connectionId: string | null;
  error: string | null;
  lastUpdate: Date | null;
  /** True when authentication failed (401) - requires re-login, not retry */
  isAuthError: boolean;
}

interface UseMyTasksWebSocketOptions {
  /** Enable/disable the WebSocket connection */
  enabled?: boolean;
  /** Callback when tasks are updated */
  onTasksUpdated?: () => void;
}

// Reconnection constants per CLAUDE.md
const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 30000;
const MAX_RETRY_ATTEMPTS = 10;
const JITTER_MAX_MS = 1000;

/**
 * Calculate exponential backoff with jitter.
 */
function calculateBackoff(attempt: number): number {
  const baseDelay = Math.min(
    INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt),
    MAX_RETRY_DELAY_MS
  );
  const jitter = Math.random() * JITTER_MAX_MS;
  return baseDelay + jitter;
}

export function useMyTasksWebSocket(options: UseMyTasksWebSocketOptions = {}) {
  const { enabled = true, onTasksUpdated } = options;

  const isOnline = useOnlineStatus();
  const queryClient = useQueryClient();

  const [state, setState] = useState<WebSocketState>({
    isConnected: false,
    isConnecting: false,
    connectionId: null,
    error: null,
    lastUpdate: null,
    isAuthError: false,
  });

  // Refs to persist across renders
  const wsRef = useRef<WebSocket | null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<number | null>(null);
  const heartbeatTimeoutRef = useRef<number | null>(null);
  const cursorRef = useRef<string | null>(null);
  const heartbeatIntervalRef = useRef<number>(30); // Default heartbeat seconds
  const isConnectingRef = useRef(false);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (retryTimeoutRef.current) {
      window.clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (heartbeatTimeoutRef.current) {
      window.clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    isConnectingRef.current = false;
  }, []);

  // Send message helper
  const sendMessage = useCallback((message: ClientMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Reset heartbeat timeout
  const resetHeartbeatTimeout = useCallback(() => {
    if (heartbeatTimeoutRef.current) {
      window.clearTimeout(heartbeatTimeoutRef.current);
    }
    // If no ping received within 2x heartbeat interval, consider connection dead
    heartbeatTimeoutRef.current = window.setTimeout(() => {
      console.warn('[WebSocket] Heartbeat timeout, reconnecting...');
      if (wsRef.current) {
        wsRef.current.close();
      }
    }, heartbeatIntervalRef.current * 2000);
  }, []);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    // Don't connect if offline, disabled, or already connected/connecting
    if (!isOnline || !enabled) {
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    // Guard against multiple simultaneous connection attempts during async ticket fetch
    if (isConnectingRef.current) return;
    isConnectingRef.current = true;

    setState(prev => ({ ...prev, isConnecting: true, error: null }));

    try {
      // Get authentication ticket
      const ticketResponse = await fetchWsTicket();
      const wsUrl = getWebSocketUrl(ticketResponse.data.ticket);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WebSocket] Connected');
        retryCountRef.current = 0; // Reset retry count on successful connection
        setState(prev => ({
          ...prev,
          isConnected: true,
          isConnecting: false,
          error: null,
        }));
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as ServerMessage;

          switch (message.type) {
            case 'hello':
              console.log('[WebSocket] Received hello:', message.connectionId);
              heartbeatIntervalRef.current = message.heartbeatSeconds;
              setState(prev => ({ ...prev, connectionId: message.connectionId }));
              resetHeartbeatTimeout();

              // Subscribe to my_tasks channel
              sendMessage({
                type: 'subscribe',
                channel: 'my_tasks',
                cursor: cursorRef.current ?? undefined,
              });
              break;

            case 'subscribed':
              console.log('[WebSocket] Subscribed to', message.channel);
              cursorRef.current = message.cursor;
              break;

            case 'ping':
              sendMessage({ type: 'pong' });
              resetHeartbeatTimeout();
              break;

            case 'schedule_updated':
              console.log('[WebSocket] Schedule updated');
              cursorRef.current = message.cursor;
              setState(prev => ({ ...prev, lastUpdate: new Date() }));

              // Invalidate React Query cache to trigger refetch
              queryClient.invalidateQueries({ queryKey: ['my-tasks'] });

              // Call optional callback
              onTasksUpdated?.();
              break;

            case 'cursor_expired':
              console.log('[WebSocket] Cursor expired, refetching full state');
              cursorRef.current = null;
              queryClient.invalidateQueries({ queryKey: ['my-tasks'] });

              // Resubscribe without cursor
              sendMessage({
                type: 'subscribe',
                channel: 'my_tasks',
              });
              break;

            case 'error':
              console.error('[WebSocket] Error:', message.code, message.message);
              setState(prev => ({ ...prev, error: message.message }));

              // Handle specific error cases
              if (message.code === 'FORBIDDEN') {
                // Workers can only subscribe to my_tasks, this shouldn't happen
                console.error('[WebSocket] Access denied to channel');
              }
              if (message.code === 'backpressure') {
                // Server kicked us off for being too slow
                ws.close();
              }
              break;
          }
        } catch (err) {
          console.error('[WebSocket] Failed to parse message:', err);
        }
      };

      ws.onclose = (event) => {
        console.log('[WebSocket] Closed:', event.code, event.reason);
        wsRef.current = null;

        if (heartbeatTimeoutRef.current) {
          window.clearTimeout(heartbeatTimeoutRef.current);
        }

        setState(prev => ({
          ...prev,
          isConnected: false,
          isConnecting: false,
          connectionId: null,
        }));

        // Don't retry if:
        // - Offline
        // - Disabled
        // - Max retries exceeded
        // - Intentional close (code 1000)
        if (!isOnline || !enabled || event.code === 1000) {
          return;
        }

        if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
          setState(prev => ({
            ...prev,
            error: 'Max reconnection attempts exceeded. Please refresh the page.',
          }));
          return;
        }

        // Schedule reconnection with exponential backoff
        const delay = calculateBackoff(retryCountRef.current);
        console.log(`[WebSocket] Reconnecting in ${Math.round(delay)}ms (attempt ${retryCountRef.current + 1})`);

        retryTimeoutRef.current = window.setTimeout(() => {
          retryCountRef.current++;
          connect();
        }, delay);
      };

      ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        // onclose will handle reconnection
      };

    } catch (err) {
      // Check for authentication failure (401) - don't retry, user needs to re-login
      if (err instanceof ApiError && err.status === 401) {
        console.warn('[WebSocket] Authentication failed (401) - not retrying');
        setState(prev => ({
          ...prev,
          isConnecting: false,
          isAuthError: true,
          error: 'Authentication required. Please refresh and sign in again.',
        }));
        isConnectingRef.current = false;
        return; // Don't retry on auth errors
      }

      console.error('[WebSocket] Failed to get ticket:', err);
      setState(prev => ({
        ...prev,
        isConnecting: false,
        error: err instanceof Error ? err.message : 'Failed to connect',
      }));

      // Schedule retry for ticket fetch failure (but not auth errors)
      if (retryCountRef.current < MAX_RETRY_ATTEMPTS && isOnline && enabled) {
        const delay = calculateBackoff(retryCountRef.current);
        console.log(`[WebSocket] Scheduling retry ${retryCountRef.current + 1}/${MAX_RETRY_ATTEMPTS} in ${Math.round(delay)}ms`);
        retryCountRef.current++;
        retryTimeoutRef.current = window.setTimeout(() => {
          connect();
        }, delay);
      } else if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
        console.warn('[WebSocket] Max retries reached, stopping');
        setState(prev => ({
          ...prev,
          error: 'Max reconnection attempts exceeded. Please refresh the page.',
        }));
      }
    } finally {
      isConnectingRef.current = false;
    }
  }, [isOnline, enabled, queryClient, onTasksUpdated, sendMessage, resetHeartbeatTimeout]);

  // Disconnect manually
  const disconnect = useCallback(() => {
    cleanup();
    retryCountRef.current = 0;
    setState({
      isConnected: false,
      isConnecting: false,
      connectionId: null,
      error: null,
      lastUpdate: null,
      isAuthError: false,
    });
  }, [cleanup]);

  // Effect to manage connection lifecycle
  useEffect(() => {
    if (enabled && isOnline && !state.isAuthError) {
      connect();
    } else if (!enabled || !isOnline) {
      // Cleanup when going offline or disabled
      cleanup();
      setState(prev => ({
        ...prev,
        isConnected: false,
        isConnecting: false,
        // Don't clear auth error here - preserve it so UI can show re-login prompt
      }));
    }

    return cleanup;
    // Note: Intentionally NOT including state.isAuthError in deps to avoid re-triggering
    // when auth error is set. The auth error state should be handled by user action (refresh).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isOnline, connect, cleanup]);

  // Effect to reconnect when coming back online (from offline state)
  // This should ONLY trigger when online status changes, not on every state change
  const wasOnlineRef = useRef(isOnline);
  useEffect(() => {
    const wasOffline = !wasOnlineRef.current;
    wasOnlineRef.current = isOnline;

    // Only reconnect if we just came back online (was offline, now online)
    // AND we're not already connected/connecting, AND no auth error
    if (wasOffline && isOnline && enabled && !state.isConnected && !state.isConnecting && !state.isAuthError) {
      console.log('[WebSocket] Back online, resetting retry count and reconnecting');
      retryCountRef.current = 0;
      connect();
    }
  }, [isOnline, enabled, state.isConnected, state.isConnecting, state.isAuthError, connect]);

  return {
    ...state,
    isOnline,
    disconnect,
    reconnect: connect,
  };
}
